/**
 * Sticky Add-to-Cart Bar
 *
 * Architecture: Intersection Observer only. No scroll event listeners.
 *
 * Dependencies:
 *   - theme.dev.js dispatches 'theme:variant:change' on variant selection
 *   - theme.dev.js dispatches 'theme:cart:open' / 'theme:cart:close' on cart drawer toggle
 *   - theme.dev.js adds 'js-drawer-open-cart' class to body when cart drawer is open
 *
 * Loaded conditionally on product pages via layout/theme.liquid.
 */

(function () {
  'use strict';

  var stickyBar = document.getElementById('sticky-atc');
  if (!stickyBar) return;

  // --- Selectors ---
  var SELECTORS = {
    mainFormWrapper: '[data-section-type="product-template"] [data-form-wrapper]',
    mainAtcBtn: '[data-section-type="product-template"] [data-add-to-cart]',
    mainAtcBtnText: '[data-section-type="product-template"] [data-add-to-cart-text]',
    mainVariantInput: '[data-section-type="product-template"] input[name="id"][data-product-select]',
    stickyVariantId: '[data-sticky-atc-variant-id]',
    stickyBtn: '[data-sticky-atc-btn]',
    stickyBtnText: '[data-sticky-atc-btn-text]',
    stickyVariantSelect: '[data-sticky-atc-variant-select]',
    stickyPrice: '[data-sticky-atc-price]',
    stickyComparePrice: '[data-sticky-atc-compare-price]',
  };

  var CLASSES = {
    visible: 'is-visible',
    suppressed: 'is-suppressed',
    cartDrawerOpen: 'js-drawer-open-cart',
    drawerOpen: 'js-drawer-open',
  };

  // Cache elements
  var mainFormWrapper = document.querySelector(SELECTORS.mainFormWrapper);
  var stickyVariantInput = stickyBar.querySelector(SELECTORS.stickyVariantId);
  var stickyBtn = stickyBar.querySelector(SELECTORS.stickyBtn);
  var stickyBtnText = stickyBar.querySelector(SELECTORS.stickyBtnText);
  var stickyVariantSelect = stickyBar.querySelector(SELECTORS.stickyVariantSelect);
  var stickyPrice = stickyBar.querySelector(SELECTORS.stickyPrice);

  if (!mainFormWrapper) return;

  // =============================================
  // 1. INTERSECTION OBSERVER -- show/hide
  // =============================================

  // Mobile fix: suppress the sticky bar on initial load until the user has
  // intentionally scrolled. On mobile the ATC form is below the fold, so the
  // IntersectionObserver fires immediately with isIntersecting=false and would
  // show the bar before the user has seen the product hero.
  var hasScrolled = false;
  var lastIsIntersecting = true; // assume visible until observer says otherwise

  window.addEventListener(
    'scroll',
    function () {
      hasScrolled = true;
      // Re-evaluate immediately with the last known intersection state so the
      // correct visible/hidden state takes effect on the very first scroll tick.
      var shouldShow = !lastIsIntersecting;
      if (shouldShow && isDrawerOpen()) return;
      stickyBar.classList.toggle(CLASSES.visible, shouldShow);
      stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    },
    { once: true, passive: true }
  );

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        lastIsIntersecting = entry.isIntersecting;
        var shouldShow = !entry.isIntersecting;

        // Do not show if a drawer is open
        if (shouldShow && isDrawerOpen()) {
          return;
        }

        // Hide path: always run so the bar disappears when the ATC comes back
        // into view (e.g. user scrolls back up).
        if (!shouldShow) {
          stickyBar.classList.toggle(CLASSES.visible, false);
          stickyBar.setAttribute('aria-hidden', 'true');
          return;
        }

        // Show path: only reveal the bar after the user has scrolled at least
        // once. This prevents the bar from covering the product hero on initial
        // page load on mobile.
        if (!hasScrolled) return;

        stickyBar.classList.toggle(CLASSES.visible, true);
        stickyBar.setAttribute('aria-hidden', 'false');
      });
    },
    {
      threshold: 0,
      rootMargin: '0px',
    }
  );

  observer.observe(mainFormWrapper);

  // =============================================
  // 2. CART DRAWER SUPPRESSION
  // =============================================
  // Using both custom events AND body class observation for robustness.
  // The theme dispatches 'theme:cart:open' and 'theme:cart:close' on document.
  // The theme also adds 'js-drawer-open-cart' to document.body.
  // We listen for both mechanisms.

  function isDrawerOpen() {
    return (
      document.body.classList.contains(CLASSES.cartDrawerOpen) ||
      document.body.classList.contains(CLASSES.drawerOpen)
    );
  }

  document.addEventListener('theme:cart:open', function () {
    stickyBar.classList.add(CLASSES.suppressed);
  });

  document.addEventListener('theme:cart:close', function () {
    stickyBar.classList.remove(CLASSES.suppressed);
  });

  // Also suppress when ANY drawer opens (size chart, search, etc.)
  var bodyObserver = new MutationObserver(function () {
    var drawerIsOpen = isDrawerOpen();
    stickyBar.classList.toggle(CLASSES.suppressed, drawerIsOpen);
  });

  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  // =============================================
  // 3. VARIANT SYNC -- main form -> sticky bar
  // =============================================
  // The theme dispatches 'theme:variant:change' on the section container.
  // Because bubbles: true, we can listen on document.

  document.addEventListener('theme:variant:change', function (event) {
    var variant = event.detail ? event.detail.variant : null;

    // Only process if the event came from the main product section,
    // not from a quickview or related products section.
    var sourceSection = event.target ? event.target.closest('[data-section-type="product-template"]') : null;
    if (!sourceSection) return;

    syncVariantToStickyBar(variant);
  });

  function syncVariantToStickyBar(variant) {
    if (!variant) {
      // Variant is unavailable (invalid option combo)
      if (stickyBtn) stickyBtn.disabled = true;
      if (stickyBtnText) stickyBtnText.textContent = window.theme.strings.unavailable;
      return;
    }

    // Update hidden variant ID input
    if (stickyVariantInput) {
      stickyVariantInput.value = variant.id;
    }

    // Update variant select dropdown (if present)
    if (stickyVariantSelect) {
      stickyVariantSelect.value = variant.id;
    }

    // Update button state
    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;
    }

    if (stickyBtnText) {
      if (variant.available) {
        stickyBtnText.textContent = window.theme.strings.add_to_cart;
      } else {
        stickyBtnText.textContent = window.theme.strings.sold_out;
      }
    }

    // Update price display
    if (stickyPrice) {
      updatePriceDisplay(variant);
    }
  }

  function updatePriceDisplay(variant) {
    var moneyFormat = window.theme.moneyFormat;
    var currencyCodeEnabled = window.theme.settings.currency_code_enable;

    var priceHtml = formatMoney(variant.price, currencyCodeEnabled);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHtml += ' <s class="sticky-atc__compare-price">' + formatMoney(variant.compare_at_price, currencyCodeEnabled) + '</s>';
    }

    stickyPrice.innerHTML = priceHtml;
  }

  function formatMoney(cents, withCurrency) {
    if (typeof themeCurrency !== 'undefined' && typeof themeCurrency.formatMoney === 'function') {
      // Use the theme's currency formatting utility (from vendor.js)
      var format = withCurrency ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
      return themeCurrency.formatMoney(cents, format);
    }

    // Fallback: basic formatting
    var amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  // =============================================
  // 4. STICKY BAR VARIANT SELECT -> main form sync
  // =============================================
  // When the user changes the variant from the sticky bar's dropdown,
  // propagate the change to the main form.

  if (stickyVariantSelect) {
    stickyVariantSelect.addEventListener('change', function () {
      var selectedVariantId = this.value;

      // Update the main form's hidden input
      var mainVariantInput = document.querySelector(SELECTORS.mainVariantInput);
      if (mainVariantInput) {
        mainVariantInput.value = selectedVariantId;
        // Trigger a change event so the theme's JS picks up the change
        mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Also update the sticky bar's own hidden input
      if (stickyVariantInput) {
        stickyVariantInput.value = selectedVariantId;
      }

      // Find the selected option to update button state directly
      var selectedOption = this.options[this.selectedIndex];
      if (selectedOption) {
        var isAvailable = selectedOption.getAttribute('data-variant-available') === 'true';
        if (stickyBtn) stickyBtn.disabled = !isAvailable;
        if (stickyBtnText) {
          stickyBtnText.textContent = isAvailable
            ? window.theme.strings.add_to_cart
            : window.theme.strings.sold_out;
        }

        // Update price from data attributes
        var price = parseInt(selectedOption.getAttribute('data-variant-price'), 10);
        var comparePrice = parseInt(selectedOption.getAttribute('data-variant-compare-price'), 10);
        if (stickyPrice && !isNaN(price)) {
          var currencyCodeEnabled = window.theme.settings.currency_code_enable;
          var priceHtml = formatMoney(price, currencyCodeEnabled);
          if (comparePrice && comparePrice > price) {
            priceHtml += ' <s class="sticky-atc__compare-price">' + formatMoney(comparePrice, currencyCodeEnabled) + '</s>';
          }
          stickyPrice.innerHTML = priceHtml;
        }
      }
    });
  }

  // =============================================
  // 5. FORM SUBMISSION -- use AJAX if cart type is drawer
  // =============================================
  var stickyForm = stickyBar.querySelector('[data-sticky-atc-form]');
  if (stickyForm) {
    stickyForm.addEventListener('submit', function (event) {
      // If cart type is 'drawer', prevent default and use AJAX
      if (window.theme.settings.cartType === 'drawer') {
        event.preventDefault();

        var formData = new FormData(stickyForm);

        fetch(window.theme.routes.cart_add_url + '.js', {
          method: 'POST',
          body: formData,
        })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Add to cart failed');
            }
            return response.json();
          })
          .then(function () {
            // Trigger the theme's cart open event
            document.dispatchEvent(
              new CustomEvent('theme:cart:open', { bubbles: true })
            );
            // Trigger the theme's cart update
            document.dispatchEvent(
              new CustomEvent('theme:cart:change', { bubbles: true })
            );
          })
          .catch(function (error) {
            console.error('Sticky ATC:', error);
            // Fallback: redirect to cart page
            window.location = window.theme.routes.cart_url;
          });
      }
      // If cart type is 'page', let the form submit naturally (default behavior)
    });
  }
})();
